import { reactive, ref, inject, computed } from "vue";
import { getJSON } from "@api/request";
import { getActivityApi } from "@utils";

const showCurErrorTips = ref();
const showSuccessTips = ref();

const physicalAwardPopupState = ref(false);
const physicalDoubleCheckPopupState = ref(false);

// 背包相关状态
const page = ref(1);
const awardLogList = ref<any[]>([]);
const total = ref(0);
const pageSize = 6;

// 实物奖励相关状态
const showToast = ref(false);

// 虚拟奖励兑换码
export const virtualAwardDetailPopupState = ref(false);
export const virtualAwardDetailCode = ref("");

// 常量定义
const TOAST_DISPLAY_DURATION = 1500; // Toast 显示时长（毫秒）
const MAX_NAME_LENGTH = 100;
const MAX_ADDRESS_LENGTH = 200;
const MAX_POSTAL_LENGTH = 10;

// 区号与手机号规则
export const AREA_CODES = [
  { code: "+66",  country: "Thailand",    minLen: 9,  maxLen: 10, labels: { "zh-CN": "泰国",       "en-US": "Thailand",    "th-TH": "ไทย",         "id-ID": "Thailand"  } },
  { code: "+62",  country: "Indonesia",   minLen: 9,  maxLen: 13, labels: { "zh-CN": "印度尼西亚", "en-US": "Indonesia",   "th-TH": "อินโดนีเซีย", "id-ID": "Indonesia" } },
  { code: "+63",  country: "Philippines", minLen: 10, maxLen: 10, labels: { "zh-CN": "菲律宾",     "en-US": "Philippines", "th-TH": "ฟิลิปปินส์",  "id-ID": "Filipina"  } },
  { code: "+65",  country: "Singapore",   minLen: 8,  maxLen: 8,  labels: { "zh-CN": "新加坡",     "en-US": "Singapore",   "th-TH": "สิงคโปร์",    "id-ID": "Singapura" } },
  { code: "+60",  country: "Malaysia",    minLen: 9,  maxLen: 11, labels: { "zh-CN": "马来西亚",   "en-US": "Malaysia",    "th-TH": "มาเลเซีย",    "id-ID": "Malaysia"  } },
  { code: "+855", country: "Cambodia",    minLen: 8,  maxLen: 9,  labels: { "zh-CN": "柬埔寨",     "en-US": "Cambodia",    "th-TH": "กัมพูชา",     "id-ID": "Kamboja"   } },
  { code: "+95",  country: "Myanmar",     minLen: 8,  maxLen: 10, labels: { "zh-CN": "缅甸",       "en-US": "Myanmar",     "th-TH": "เมียนมาร์",   "id-ID": "Myanmar"   } },
  { code: "+856", country: "Laos",        minLen: 8,  maxLen: 9,  labels: { "zh-CN": "老挝",       "en-US": "Laos",        "th-TH": "ลาว",          "id-ID": "Laos"      } },
];

// 国家/地区列表
export const COUNTRIES = [
  { key: "Thailand",    labels: { "zh-CN": "泰国",       "en-US": "Thailand",    "th-TH": "ไทย",         "id-ID": "Thailand"  } },
  { key: "Indonesia",   labels: { "zh-CN": "印度尼西亚", "en-US": "Indonesia",   "th-TH": "อินโดนีเซีย", "id-ID": "Indonesia" } },
  { key: "Philippines", labels: { "zh-CN": "菲律宾",     "en-US": "Philippines", "th-TH": "ฟิลิปปินส์",  "id-ID": "Filipina"  } },
  { key: "Singapore",   labels: { "zh-CN": "新加坡",     "en-US": "Singapore",   "th-TH": "สิงคโปร์",    "id-ID": "Singapura" } },
  { key: "Malaysia",    labels: { "zh-CN": "马来西亚",   "en-US": "Malaysia",    "th-TH": "มาเลเซีย",    "id-ID": "Malaysia"  } },
  { key: "Cambodia",    labels: { "zh-CN": "柬埔寨",     "en-US": "Cambodia",    "th-TH": "กัมพูชา",     "id-ID": "Kamboja"   } },
  { key: "Myanmar",     labels: { "zh-CN": "缅甸",       "en-US": "Myanmar",     "th-TH": "เมียนมาร์",   "id-ID": "Myanmar"   } },
  { key: "Laos",        labels: { "zh-CN": "老挝",       "en-US": "Laos",        "th-TH": "ลาว",          "id-ID": "Laos"      } },
];

const viewCode = async (item) => {
  const id = item.ID;
  const res = await getJSON(
    getActivityApi(
      window.platform_id,
      window.activity_name,
      "virtualAwardDetail"
    ),
    {
      params: JSON.stringify({
        id,
      }),
    }
  );
  if (res.result === 1 && res.data?.awardValue) {
    virtualAwardDetailCode.value = res.data.awardValue;
    virtualAwardDetailPopupState.value = true;
  } else {
    showCurErrorTips.value?.(res.code);
  }
};

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(total.value / pageSize) || 1;
});

// 计算要显示的页码
const displayPages = computed(() => {
  const pages: (number | string)[] = [];
  const totalPage = totalPages.value;

  if (totalPage <= 5) {
    // 总页数不超过5页，全部显示
    for (let i = 1; i <= totalPage; i++) {
      pages.push(i);
    }
  } else {
    // 总页数超过5页，显示前2页+省略号+后3页
    for (let i = 1; i <= 2; i++) {
      pages.push(i);
    }
    pages.push("...");
    for (let i = totalPage - 2; i <= totalPage; i++) {
      pages.push(i);
    }
  }

  return pages;
});

const fetchAwardList = async () => {
  let res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "myAward"),
    {
      params: JSON.stringify({
        page: page.value,
        pageSize: pageSize,
      }),
    }
  );

  if (res.result === 1) {
    awardLogList.value = res.data.awardData || [];
    total.value = res.data.total || 0;
  } else {
    showCurErrorTips.value?.(res.code);
  }

  return res;
};

// 切换页码
const switchPage = (pageNum: number) => {
  page.value = pageNum;
  fetchAwardList();
};

// 初始化背包列表
const initAwardList = async () => {
  awardLogList.value = [];
  page.value = 1;
  await fetchAwardList();
};

// 重置背包列表
const resetAwardList = () => {
  awardLogList.value = [];
  page.value = 1;
};

const physicalAwardUserState = reactive({
  phone: "",
  areaCode: "+66",
  name: "",
  address: "",
  country: "Thailand",
  postalCode: "",
  hasPhoneError: false,
  hasNameError: false,
  hasAddressError: false,
  hasPostalCodeError: false,
});

// 虚拟奖励
const getVirtualAward = async () => {
  const res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "virtualAward"),
    {}
  );
  if (res.result === 1) {
    showSuccessTips.value?.("claimVirtualRewardSuccess");
    // 领奖成功后刷新背包列表
    await initAwardList();
    // 重新请求basic接口
    return true;
  } else {
    showCurErrorTips.value?.(res?.code);
    return false;
  }
};

const getPhysicalAward = async (params: {
  phone: string;
  name: string;
  address: string;
  postCode: string;
  phoneCode: string;
  area: string;
}) => {
  const res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "materialAward"),
    {
      params: JSON.stringify(params),
    }
  );
  return res;
};

// 实物奖励
const fillPhysicalAward = () => {
  physicalAwardPopupState.value = true;
  window.activityDataLayer.push({ eventName: "show_popup", eventType: "实物奖励弹窗", aid: window.activity_name, gameId: window.game_id });
};

// 字段验证辅助函数
const validateField = (
  value: string,
  maxLength: number,
  errorStateKey: "hasNameError" | "hasPhoneError" | "hasAddressError" | "hasPostalCodeError"
): boolean => {
  const isValid = !!(value && value.length > 0 && value.length <= maxLength);
  (physicalAwardUserState as any)[errorStateKey] = !isValid;
  return isValid;
};

const doubleCheckPhysicalAward = () => {
  const isNameValid = validateField(physicalAwardUserState.name, MAX_NAME_LENGTH, "hasNameError");
  if (!isNameValid) return;

  // 手机号：按区号规则校验位数
  const rule = AREA_CODES.find(a => a.code === physicalAwardUserState.areaCode);
  const phoneLen = physicalAwardUserState.phone.length;
  const isPhoneValid = rule
    ? phoneLen >= rule.minLen && phoneLen <= rule.maxLen
    : phoneLen > 0;
  physicalAwardUserState.hasPhoneError = !isPhoneValid;
  if (!isPhoneValid) return;

  const isAddressValid = validateField(physicalAwardUserState.address, MAX_ADDRESS_LENGTH, "hasAddressError");
  if (!isAddressValid) return;

  const isPostalValid = validateField(physicalAwardUserState.postalCode, MAX_POSTAL_LENGTH, "hasPostalCodeError");
  if (!isPostalValid) return;

  physicalAwardPopupState.value = false;
  physicalDoubleCheckPopupState.value = true;
};

const cancelDoubleCheckPhysicalAward = () => {
  physicalDoubleCheckPopupState.value = false;
  physicalAwardPopupState.value = true;
};

const submitPhysicalAward = async () => {
  const res = await getPhysicalAward({
    phone: encodeURIComponent(physicalAwardUserState.phone),
    name: encodeURIComponent(physicalAwardUserState.name),
    address: encodeURIComponent(physicalAwardUserState.country + " " + physicalAwardUserState.address + " " + physicalAwardUserState.postalCode),
    postCode: physicalAwardUserState.postalCode,
    phoneCode: physicalAwardUserState.areaCode.replace("+", ""),
    area: physicalAwardUserState.country.toLowerCase(),
  });
  // let res = {result: 1}
  if (res.result === 1) {
    showToast.value = true;
    setTimeout(async () => {
      showToast.value = false;
      physicalDoubleCheckPopupState.value = false;
      resetPhysicalAwardForm();
      // 领奖成功后刷新背包列表
      await initAwardList();
    }, TOAST_DISPLAY_DURATION);
    return true;
  } else {
    showCurErrorTips.value?.(res?.code);
    return false;
  }
};

// 重置实物奖励表单
const resetPhysicalAwardForm = () => {
  physicalAwardUserState.phone = "";
  physicalAwardUserState.areaCode = "+66";
  physicalAwardUserState.name = "";
  physicalAwardUserState.address = "";
  physicalAwardUserState.country = "Thailand";
  physicalAwardUserState.postalCode = "";
  physicalAwardUserState.hasPhoneError = false;
  physicalAwardUserState.hasNameError = false;
  physicalAwardUserState.hasAddressError = false;
  physicalAwardUserState.hasPostalCodeError = false;
};

export default function useGetAward(
  funcShowSuccessTips = null,
  funcShowCurErrorTips = null
) {
  if (funcShowSuccessTips) {
    showSuccessTips.value = funcShowSuccessTips;
  }
  if (funcShowCurErrorTips) {
    showCurErrorTips.value = funcShowCurErrorTips;
  }
  return {
    // 背包相关
    page,
    awardLogList,
    total,
    totalPages,
    displayPages,
    switchPage,
    initAwardList,
    resetAwardList,
    // 领奖相关
    physicalAwardPopupState,
    physicalDoubleCheckPopupState,
    physicalAwardUserState,
    fillPhysicalAward,
    doubleCheckPhysicalAward,
    cancelDoubleCheckPhysicalAward,
    submitPhysicalAward,
    resetPhysicalAwardForm,
    getVirtualAward,
    getPhysicalAward,
    showToast,
    virtualAwardDetailPopupState,
    virtualAwardDetailCode,
    viewCode,
  };
}
